<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <slot name="trigger" :openModal="openModal"></slot>
  <Teleport to="body">
    <transition
      enter-active-class=""
      enter-from-class="modal-opacity-0"
      enter-to-class="modal-opacity-100"
      leave-active-class=""
      leave-from-class="modal-opacity-100"
      leave-to-class="modal-opacity-0"
    >
      <div
        v-show="isOpen && isEnabled"
        :class="['base-model', modalClass]"
        :style="styleObject"
        @click="
          (e) => {
            props.actionRequired === false && dismissModalHandler(e);
          }
        "
      >
        <transition
          enter-active-class="slide-left-enter-active"
          enter-from-class="slide-left-enter"
          enter-to-class=""
          leave-active-class="slide-left-enter-active"
          leave-from-class=".slide-left-leave-to"
          leave-to-class="slide-left-leave-to"
        >
          <div
            v-show="isOpen && isEnabled"
            ref="modal"
            role="dialog"
            aria-modal="true"
            class="base-model__inner"
          >
            <slot name="modal-content" :closeModal="closeModal"></slot>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import {
  ref,
  watch,
  inject,
  defineEmits,
  onMounted,
  computed,
  defineExpose,
  onBeforeMount,
} from 'vue';
import windowScrollLockHandler from '../../hooks/scrollLock';
import { useCookies } from '../../hooks/useCookies';

const { getCookie, setCookie } = useCookies();

const props = defineProps({
  openOnMount: {
    type: Boolean,
    default: false,
  },
  modalClass: {
    type: String,
    default: '',
  },
  elmIdToFocus: {
    type: String,
    default: '',
  },
  triggerIdToFocus: {
    type: String,
    default: '',
  },
  injectionName: {
    type: String,
    default: 'injectedIsOpen',
  },
  actionRequired: {
    type: Boolean,
    default: false,
  },
  additionalStyles: {
    type: Object,
    default: () => {
      return { color: 'red', fontSize: '13px' };
    },
  },
  isEnabled: {
    type: Boolean,
    default: true,
  },
  cookieName: {
    type: String,
    default: '',
  },
  cookieExpiry: {
    type: Number,
    default: 1,
  },
});

const isOpen = ref(false);
const modal = ref(null);
const trigger = ref(null);
const injectedIsOpen = inject(props.injectionName);
const emit = defineEmits(['update:state']);

const styleObject = computed(() => props.additionalStyles);

const setModalCookie = () => {
  if (!props.cookieName || !props.cookieExpiry) return;

  // Ensure that the expiry is a positive number
  if (props.cookieExpiry <= 0) {
    console.error('cookieExpiry must be a positive number of days.');
    return;
  }

  // Set the cookie using the number of days directly.
  setCookie(props.cookieName, props.cookieExpiry, true);
};

const checkModalCookie = () => {
  if (!props.cookieName) return;

  const hasCookie = getCookie(props.cookieName);
  if (hasCookie) {
    isOpen.value = false; // Prevent modal from opening if cookie exists
  }
};

const lockNavigationHandler = (e) => {
  const focusableElements = modal.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (
    e.key === 'Tab' &&
    !e.shiftKey &&
    document.activeElement === focusableElements[focusableElements.length - 1]
  ) {
    e.preventDefault();
    focusableElements[0].focus();
  } else if (
    e.key === 'Tab' &&
    e.shiftKey &&
    document.activeElement === focusableElements[0]
  ) {
    e.preventDefault();
    focusableElements[focusableElements.length - 1].focus();
  }
};

const setInertHandler = (bool) => {
  const pageContent = document.querySelectorAll('body > *:not(.base-model)');
  pageContent.forEach((element) => {
    if (bool) {
      element.setAttribute('inert', bool);
      element.setAttribute('aria-hidden', bool);
    } else {
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
    }
  });
};

const openModal = (e) => {
  if (e) {
    trigger.value = e.target;
  }
  isOpen.value = true;
  windowScrollLockHandler(true);
};

const closeModal = () => {
  isOpen.value = false;
  windowScrollLockHandler(false);
  setModalCookie(); // Set cookie on dismissal
  emit('update:state', 'isClosed');
};

const dismissModalHandler = (e) => {
  if (e.target.classList.contains('base-model')) {
    closeModal();
  }
};

defineExpose({
  closeModal,
  openModal,
});

onBeforeMount(() => {
  checkModalCookie(); // Check if the modal should be suppressed based on cookie

  // Open the modal if openOnMount is true and no suppressing cookie exists
  if (props.openOnMount && !getCookie(props.cookieName)) {
    isOpen.value = true;
  }
});

onMounted(() => {
  setInertHandler(false);
});

watch(
  () => isOpen.value,
  (newVal) => {
    if (newVal) {
      setTimeout(() => {
        let focusItem = modal.value.querySelector(
          props.elmIdToFocus ||
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        focusItem?.focus();
        setInertHandler(true);
        modal.value.addEventListener('keydown', lockNavigationHandler);
      }, 100);
    } else {
      setInertHandler(false);
      modal.value?.removeEventListener('keydown', lockNavigationHandler);
      trigger.value?.focus();
    }
  },
);

watch(
  () => injectedIsOpen?.value,
  (newVal) => {
    isOpen.value = newVal;
  },
);
</script>
